import { toNumber } from 'lodash'

import { createAuditEnterpriseApi } from '@eastcoal/api/auth/audit/createAuditEnterprise'
import { queryAuditEnterpriseDetailByMemberIdApi } from '@eastcoal/api/auth/audit/queryAuditEnterpriseDetailByMemberId'
import { updateAuditEnterpriseApi } from '@eastcoal/api/auth/audit/updateAuditEnterprise'
import {
  AuditEnterpriseEntity,
  CertificationForm
} from '@eastcoal/api/auth/interface'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'
import { pick } from '@eastcoal/common/utils/object'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'
import { useNavigate } from '@eastcoal/router/exports/react-router'

import { AUDIT_ENTERPRISE_RESULT_PAGE_PATH } from '../constants/path'

import { AuditEnterpriseFormAnchor } from './components/FormAnchor'
import { agentOssIdEffect } from './effects/agentOssId'
import {
  AuditEnterpriseDetail,
  AuditEnterpriseFormExposedProps,
  AuditEnterpriseFormProps
} from './interface'
import { auditEnterpriseFormSchema } from './schema'

const formatDetail = (detail: AuditEnterpriseEntity) => {
  return {
    ...detail,
    certifications: detail.certifications?.map(item => ({
      ...item,
      timeType: `${item.timeType}`
    })),
    previousAgentId: detail.agentId
  } as AuditEnterpriseDetail
}

const createApi: AuditEnterpriseFormProps['createApi'] = (
  values,
  submitContext
) => {
  return createAuditEnterpriseApi({
    ...values,
    certifications: values.certifications?.map(
      item =>
        ({
          ...item,
          timeType: toNumber(item.timeType)
        }) as CertificationForm
    ),
    formStatus: submitContext.submitType
  })
}

const updateApi: AuditEnterpriseFormProps['updateApi'] = (
  values,
  submitContext
) => {
  return updateAuditEnterpriseApi({
    ...values,
    formStatus: submitContext.submitType
  })
}

const readApi: AuditEnterpriseFormProps['readApi'] = context => {
  const memberId = context?.memberId

  return {
    api: async () => {
      const data = await queryAuditEnterpriseDetailByMemberIdApi({ memberId })
      return formatDetail(data)
    },
    ready: !!memberId
  }
}

export const AuditEnterpriseForm = ({
  mode
}: AuditEnterpriseFormExposedProps) => {
  const {
    user: { name }
  } = useAuthStore(state => pick(state, ['user']))

  const navigate = useNavigate()
  const handleSuccess = () => {
    navigate(AUDIT_ENTERPRISE_RESULT_PAGE_PATH)
  }

  return (
    <PageContainer strimTopPadding>
      <CrudForm
        context={{ userName: name }}
        createApi={createApi}
        effects={agentOssIdEffect}
        grid={false}
        header={<AuditEnterpriseFormAnchor />}
        mode={mode}
        name='auditEnterpriseForm'
        readApi={readApi}
        schema={auditEnterpriseFormSchema}
        updateApi={updateApi}
        actions={[
          <BackButton key='backButton' />,
          <SaveButton key='saveButton' />,
          <SubmitButton key='submitButton' />
        ]}
        layout={{
          wrapperWidth: 400
        }}
        styles={{
          body: {
            width: 1000,
            margin: '0 auto',
            padding: 0
          }
        }}
        onSuccess={handleSuccess}
      />
    </PageContainer>
  )
}
